import { useEffect, useMemo, useState } from "react";
import { coverStyle } from "@/lib/cover";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Heart, Plus, LogOut, Calendar, MapPin, Images, Users, Sparkles, ArrowRight, Search, Crown, Ticket } from "lucide-react";

export default function Memories() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => { api.get("/events").then(({ data }) => setData(data)).catch(() => setData({ hosting: [], attending: [] })); }, []);
  const handleLogout = () => { logout(); navigate("/login"); };

  const all = useMemo(() => {
    if (!data) return [];
    return [
      ...(data.hosting || []).map((e) => ({ ...e, _role: "host" })),
      ...(data.attending || []).map((e) => ({ ...e, _role: "guest" })),
    ];
  }, [data]);

  const { upcoming, past } = useMemo(() => {
    const s = q.trim().toLowerCase();
    const filtered = all.filter((e) => !s || `${e.title} ${e.venue_name || ""} ${e.type_name || ""}`.toLowerCase().includes(s));
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const isPast = (e) => e.date && new Date(e.date) < today;
    const up = filtered.filter((e) => !isPast(e)).sort((a, b) => (a.date || "9999-99-99") < (b.date || "9999-99-99") ? -1 : 1);
    const pa = filtered.filter(isPast).sort((a, b) => (a.date || "") > (b.date || "") ? -1 : 1);
    return { upcoming: up, past: pa };
  }, [all, q]);

  return (
    <div className="min-h-screen" style={{ background: "#F5F4F0" }} data-testid="memories-page">
      <header className="border-b border-[#E8E6E1] bg-white/70 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="brand-home" className="flex items-center gap-2"><Heart size={18} className="text-[#8C9A86]" fill="#8C9A86" /><span className="font-serif text-2xl tracking-tight">Evermore</span></Link>
          <div className="flex items-center gap-4">
            <span className="text-sm text-stone-500 hidden sm:inline">{user?.name}</span>
            <Link to="/contacts" data-testid="contacts-link" className="text-sm text-stone-500 hover:text-[#8C9A86]">Contacts</Link>
            <Link to="/account" data-testid="account-link" className="text-sm text-stone-500 hover:text-[#8C9A86]">Account</Link>
            <button onClick={handleLogout} data-testid="logout-btn" className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-[#C67A7A]"><LogOut size={15} /> Sign out</button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10 lg:py-14">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-2">
          <div>
            <p className="overline">Your milestones</p>
            <h1 className="font-serif text-4xl sm:text-5xl tracking-tight">A lifetime of memories</h1>
            <p className="text-stone-500 mt-3 max-w-xl">Every celebration you host or attend, kept in one place. Revisit the photos and details forever.</p>
          </div>
          <Link to="/create" data-testid="create-event-btn" className="flex items-center gap-2 rounded-full bg-[#2C2A28] text-white px-6 py-3 text-sm hover:bg-[#8C9A86] transition-colors"><Plus size={16} /> Create an event</Link>
        </div>

        <div className="relative max-w-md mt-6">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search your milestones…" data-testid="milestone-search"
            className="w-full rounded-full border border-[#E8E6E1] bg-white pl-10 pr-4 py-2.5 text-sm outline-none focus:border-[#8C9A86]" />
        </div>

        {!data ? (
          <p className="text-stone-400 mt-10">Loading…</p>
        ) : (
          <>
            <Section title="Upcoming" count={upcoming.length} testid="upcoming-section" empty="No upcoming milestones. Create your next event!">
              {upcoming.map((e) => <EventCard key={`${e._role}-${e.id}`} e={e} />)}
            </Section>
            <Section title="Past" count={past.length} testid="past-section" empty="Your past celebrations will live here forever, with all their photos and details.">
              {past.map((e) => <EventCard key={`${e._role}-${e.id}`} e={e} />)}
            </Section>
          </>
        )}
      </div>
    </div>
  );
}

function Section({ title, children, empty, testid, count }) {
  const arr = Array.isArray(children) ? children.filter(Boolean) : (children ? [children] : []);
  return (
    <section className="mt-12" data-testid={testid}>
      <h2 className="font-serif text-2xl mb-5">{title} {count > 0 && <span className="text-stone-300 text-lg">· {count}</span>}</h2>
      {arr.length === 0
        ? <div className="bg-white border border-dashed border-[#E8E6E1] rounded-xl p-10 text-center text-sm text-stone-400">{empty}</div>
        : <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">{children}</div>}
    </section>
  );
}

function EventCard({ e }) {
  const host = e._role === "host";
  return (
    <Link to={host ? `/e/${e.id}/dashboard` : `/w/${e.public_slug}`} data-testid={`event-card-${e.id}`}
      className="group bg-white border border-[#E8E6E1] rounded-xl overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300">
      <div className="relative h-40 overflow-hidden">
        <img src={e.cover_image_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" style={coverStyle(e, { allowZoom: false })} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        <span className="absolute top-3 left-3 text-[11px] px-2.5 py-1 rounded-full bg-white/90 text-stone-700">{e.type_name}</span>
        <span data-testid={`role-badge-${e.id}`}
          className={`absolute top-3 right-3 text-[11px] px-2.5 py-1 rounded-full flex items-center gap-1 ${host ? "bg-[#8C9A86] text-white" : "bg-white/90 text-stone-700"}`}>
          {host ? <Crown size={11} /> : <Ticket size={11} />} {host ? "Host" : "Guest"}
        </span>
        <h3 className="absolute bottom-3 left-4 right-4 font-serif text-2xl text-white truncate">{e.title}</h3>
      </div>
      <div className="p-4">
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-stone-500">
          {e.date && <span className="flex items-center gap-1.5"><Calendar size={12} /> {formatDate(e.date)}</span>}
          {e.venue_name && <span className="flex items-center gap-1.5 truncate"><MapPin size={12} /> {e.venue_name}</span>}
        </div>
        <div className="flex items-center justify-between mt-4">
          <div className="flex gap-3 text-xs text-stone-400 items-center">
            <span className="flex items-center gap-1"><Images size={13} /> {e.photo_count}</span>
            {host && <span className="flex items-center gap-1"><Users size={13} /> {e.guest_count}</span>}
            {host && !e.published && <span className="flex items-center gap-1 text-[#C6A15B]"><Sparkles size={11} /> Draft</span>}
          </div>
          <span className="flex items-center gap-1 text-xs text-[#8C9A86] group-hover:gap-2 transition-all">{host ? "Manage" : "View memories"} <ArrowRight size={13} /></span>
        </div>
      </div>
    </Link>
  );
}

function formatDate(d) {
  try { return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return d; }
}
